import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { PeakCell } from '../../../services/reportService';
import { tokens } from '../../../theme';
import { countLabel } from '../../../utils/format';
import { formatPLN } from './statsShared';

// Monday-first weekday order; index = getUTCDay() value.
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DAY_LABELS: Record<number, string> = { 1: 'Pn', 2: 'Wt', 3: 'Śr', 4: 'Cz', 5: 'Pt', 6: 'Sb', 0: 'Nd' };
const DAY_NAMES: Record<number, string> = {
  1: 'Poniedziałek', 2: 'Wtorek', 3: 'Środa', 4: 'Czwartek', 5: 'Piątek', 6: 'Sobota', 0: 'Niedziela',
};

// Skala jednego koloru: od najjasniejszej sliwki do pelnej sliwki (liniowo w RGB).
const hexToRgb = (hex: string) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const LOW = hexToRgb(tokens.plumSofter);
const HIGH = hexToRgb(tokens.plum);
const heat = (t: number) => {
  const k = Math.max(0, Math.min(1, t));
  const [r, g, b] = LOW.map((c, i) => Math.round(c + (HIGH[i] - c) * k));
  return `rgb(${r}, ${g}, ${b})`;
};
// Najslabsza niezerowa komorka nie moze zlac sie z pusta, dlatego skala startuje od 0,18.
const scaleOf = (ratio: number) => 0.18 + ratio * 0.82;

const pad = (h: number) => String(h).padStart(2, '0');

interface Props {
  matrix: PeakCell[];
  metric: 'revenue' | 'bookings';
  compact?: boolean;
}

export const PeakHeatmap: React.FC<Props> = ({ matrix, metric, compact = false }) => {
  const hoursPresent = matrix.filter((c) => c.bookings > 0 || c.revenue > 0).map((c) => c.hour);
  const minHour = hoursPresent.length ? Math.min(...hoursPresent) : 8;
  const maxHour = hoursPresent.length ? Math.max(...hoursPresent) : 19;
  const hours: number[] = [];
  for (let h = minHour; h <= maxHour; h++) hours.push(h);

  const lookup = new Map<string, PeakCell>();
  for (const c of matrix) lookup.set(`${c.dayOfWeek}-${c.hour}`, c);

  const maxVal = Math.max(1, ...matrix.map((c) => c[metric]));
  const cell = compact ? 22 : 36;
  const gap = compact ? 2 : 4;
  const labelCol = compact ? 28 : 40;

  return (
    <Box>
      {/* Na waskim ekranie siatka przewija sie w bok wewnatrz karty; kolumna dni zostaje przyklejona. */}
      <Box sx={{ overflowX: 'auto', pb: 0.5, WebkitOverflowScrolling: 'touch' }} role="region" aria-label="Mapa godzin szczytu" tabIndex={0}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `${labelCol}px repeat(${hours.length}, minmax(${cell}px, 1fr))`,
            gap: `${gap}px`,
            width: '100%',
            minWidth: 'max-content',
          }}
        >
          <Box sx={{ position: 'sticky', left: 0, bgcolor: tokens.paper, zIndex: 1, boxShadow: `${gap}px 0 0 ${tokens.paper}` }} />
          {hours.map((h) => (
            <Typography
              key={`h-${h}`}
              sx={{ fontSize: '0.75rem', fontWeight: 600, color: tokens.muted, textAlign: 'center', fontVariantNumeric: 'tabular-nums', lineHeight: '20px' }}
            >
              {compact && h % 3 !== 0 ? '' : pad(h)}
            </Typography>
          ))}

          {DAY_ORDER.map((dow) => (
            <React.Fragment key={dow}>
              <Typography
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  bgcolor: tokens.paper,
                  boxShadow: `${gap}px 0 0 ${tokens.paper}`,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: tokens.inkSoft,
                  display: 'flex',
                  alignItems: 'center',
                  height: cell,
                }}
              >
                {DAY_LABELS[dow]}
              </Typography>
              {hours.map((h) => {
                const c = lookup.get(`${dow}-${h}`);
                const val = c ? c[metric] : 0;
                const hasAny = !!c && (c.bookings > 0 || c.revenue > 0);
                return (
                  <Tooltip
                    key={h}
                    title={
                      <>
                        <Box component="span" sx={{ display: 'block', fontWeight: 700 }}>
                          {DAY_NAMES[dow]}, {pad(h)}:00–{pad(h + 1)}:00
                        </Box>
                        {hasAny ? `${countLabel(c!.bookings, 'wizyta', 'wizyty', 'wizyt')} · ${formatPLN(c!.revenue)}` : 'Brak wizyt i sprzedaży'}
                      </>
                    }
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: cell,
                        borderRadius: compact ? '4px' : '7px',
                        bgcolor: val > 0 ? heat(scaleOf(val / maxVal)) : tokens.surface,
                        boxShadow: val > 0 ? 'none' : `inset 0 0 0 1px ${tokens.line}`,
                        cursor: 'default',
                        '&:hover': { outline: `2px solid ${tokens.plumDark}`, outlineOffset: 1 },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {!compact && hours.length > 7 && (
        <Typography sx={{ display: { xs: 'block', sm: 'none' }, fontSize: '0.8125rem', color: tokens.muted, mt: 1 }}>
          Przesuń mapę w bok, aby zobaczyć kolejne godziny.
        </Typography>
      )}

      {!compact && (
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" gap={0.75} sx={{ mr: 1.5 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: tokens.surface, boxShadow: `inset 0 0 0 1px ${tokens.line}` }} />
            <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>Brak</Typography>
          </Stack>
          <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>Mniej</Typography>
          {[0, 0.25, 0.5, 0.75, 1].map((i) => (
            <Box key={i} sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: heat(scaleOf(i)) }} />
          ))}
          <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>Więcej</Typography>
        </Stack>
      )}
    </Box>
  );
};
